/**
 * PaymentMethodChooser
 *
 * Landing screen rendered by PaymentPage when a payment link has BOTH
 * `cardEnabled` and `cryptoEnabled` set to true. The customer picks one
 * method; the chosen rail's body then takes over the page.
 *
 * Picking "Pay with Crypto" calls `cryptoPaymentService.commit(token)`
 * which (a) lazily allocates a per-order BitGo address on the backend
 * for mixed-rail links, and (b) returns the existing pre-generated
 * address for crypto-only links (idempotent on the server). The
 * resolved cryptoDefault is bubbled up to PaymentPage so CryptoPayTab
 * can render the QR immediately.
 *
 * Card pick is instant — no backend call needed; OnrampPayTab handles
 * the rest of the card flow.
 */

import React, { useState } from 'react';
import cryptoPaymentService from '../services/cryptoPaymentService.js';
import './PaymentMethodChooser.css';

const formatAmount = (amount, currency) => {
  if (amount == null) return '';
  const symbols = { USD: '$', EUR: '€', GBP: '£', INR: '₹', AUD: 'A$', CAD: 'C$' };
  const symbol = symbols[currency] || '';
  const n = Number(amount);
  if (!Number.isFinite(n)) return `${symbol}${amount}`;
  return `${symbol}${n.toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
};

const formatCryptoAmount = (amount) => {
  if (amount == null) return '';
  const n = Number(amount);
  if (!Number.isFinite(n)) return amount;
  return n.toFixed(Math.min(8, 6)).replace(/0+$/, '').replace(/\.$/, '') || '0';
};

export default function PaymentMethodChooser({ token, session, onPick }) {
  const [committing, setCommitting] = useState(false);
  const [error, setError] = useState(null);

  const handleCard = () => {
    onPick({ method: 'card' });
  };

  const handleCrypto = async () => {
    setError(null);
    setCommitting(true);
    try {
      const result = await cryptoPaymentService.commit(token);
      // Backend returns AddressResponseDto. Bubble it up so PaymentPage
      // can merge into session.cryptoDefault and render CryptoPayTab.
      onPick({
        method: 'crypto',
        cryptoDefault: {
          tokenId: result.tokenId,
          tokenSymbol: result.tokenSymbol,
          network: result.network,
          networkDisplayName: result.networkDisplayName,
          depositAddress: result.depositAddress,
          expectedAmount: result.expectedAmount,
          decimals: result.decimals,
          qrPayload: result.qrPayload,
          minConfirmations: result.minConfirmations,
        },
      });
    } catch (err) {
      setError(err?.message || 'Failed to prepare crypto payment. Please try again.');
      setCommitting(false);
    }
  };

  const cryptoDefault = session?.cryptoDefault;
  const fiatLabel = formatAmount(session?.amount, session?.currency);
  const cryptoLabel =
    cryptoDefault?.expectedAmount && cryptoDefault?.tokenSymbol
      ? `~${formatCryptoAmount(cryptoDefault.expectedAmount)} ${cryptoDefault.tokenSymbol}${
          cryptoDefault.networkDisplayName ? ` on ${cryptoDefault.networkDisplayName}` : ''
        }`
      : 'Send USDT/USDC from your wallet';

  return (
    <div className="method-chooser">
      <div className="method-chooser-header">
        <h2 className="method-chooser-title">How would you like to pay?</h2>
        {fiatLabel && (
          <div className="method-chooser-amount">
            {fiatLabel} <span>{session?.currency}</span>
          </div>
        )}
      </div>

      <button
        type="button"
        className="method-chooser-option"
        onClick={handleCard}
        disabled={committing}
        aria-label="Pay with card or bank"
      >
        <span className="method-chooser-icon">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <rect x="2" y="6" width="20" height="13" rx="2" />
            <line x1="2" y1="11" x2="22" y2="11" />
            <line x1="6" y1="15" x2="10" y2="15" />
          </svg>
        </span>
        <span className="method-chooser-info">
          <span className="method-chooser-label">Card / Bank</span>
          <span className="method-chooser-sub">
            Credit / debit card, UPI, bank transfer
          </span>
        </span>
        <span className="method-chooser-arrow">›</span>
      </button>

      <button
        type="button"
        className="method-chooser-option method-chooser-option-crypto"
        onClick={handleCrypto}
        disabled={committing}
        aria-label="Pay with cryptocurrency"
      >
        <span className="method-chooser-icon">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="12" cy="12" r="9" />
            <path d="M9 8h5a3 3 0 010 6H9V8z" />
            <path d="M9 14h6a3 3 0 010 6H9v-6z" />
          </svg>
        </span>
        <span className="method-chooser-info">
          <span className="method-chooser-label">Pay with Crypto</span>
          <span className="method-chooser-sub">{cryptoLabel}</span>
        </span>
        {committing ? (
          <span className="method-chooser-spinner" aria-hidden="true" />
        ) : (
          <span className="method-chooser-arrow">›</span>
        )}
      </button>

      {error && <div className="method-chooser-error">{error}</div>}

      <div className="method-chooser-footer">
        Secured by{' '}
        <a href="https://kubercrypto.io" target="_blank" rel="noopener noreferrer">
          KuberCrypto
        </a>
      </div>
    </div>
  );
}
